import { Text } from "@microbeta/ui";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdFullscreen,
  MdZoomIn,
  MdZoomOut,
} from "react-icons/md";
import type { ImageItem } from "../../../services/imageUtils";
import { ResponsiveImage } from "../ResponsiveImage/ResponsiveImage";
import styles from "./ImageGallery.module.scss";

export interface ImageGalleryProps {
  images?: string[];
  imageVariants?: ImageItem[];
  alt: string;
  className?: string;
  initialIndex?: number;
  showThumbnails?: boolean;
  aspectRatio?: "square" | "landscape" | "portrait" | "auto";
  descriptions?: string[];
  headerContent?: React.ReactNode;
  hideFullscreenButton?: boolean;
  onClose?: () => void;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  imageVariants,
  alt,
  className = "",
  initialIndex = 0,
  showThumbnails = true,
  aspectRatio = "landscape",
  descriptions,
  headerContent,
  hideFullscreenButton = false,
  onClose,
}) => {
  const { t } = useTranslation();

  // Determine if we have any images to display
  const hasVariants = imageVariants && imageVariants.length > 0;
  const hasImages = images && images.length > 0;
  const totalImages = hasVariants
    ? imageVariants.length
    : hasImages
      ? images.length
      : 0;

  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isFullscreen, setIsFullscreen] = useState(!!onClose);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Navigation functions
  const goToNext = useCallback(() => {
    if (totalImages > 1) {
      setCurrentIndex((prev) => (prev + 1) % totalImages);
      setZoomLevel(1);
      setDragPosition({ x: 0, y: 0 });
      setIsZoomed(false);
    }
  }, [totalImages]);

  const goToPrevious = useCallback(() => {
    if (totalImages > 1) {
      setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
      setZoomLevel(1);
      setDragPosition({ x: 0, y: 0 });
      setIsZoomed(false);
    }
  }, [totalImages]);

  // Zoom functions
  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
    setIsZoomed(true);
  }, []);

  const zoomOut = useCallback(() => {
    const newZoom = Math.max(zoomLevel - 0.5, 1);
    setZoomLevel(newZoom);
    if (newZoom === 1) {
      setIsZoomed(false);
      setDragPosition({ x: 0, y: 0 });
    }
  }, [zoomLevel]);

  const resetZoom = useCallback(() => {
    setZoomLevel(1);
    setIsZoomed(false);
    setDragPosition({ x: 0, y: 0 });
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isFullscreen) return;

      switch (event.key) {
        case "Escape":
          onClose ? onClose() : setIsFullscreen(false);
          break;
        case "ArrowLeft":
          event.preventDefault();
          goToPrevious();
          break;
        case "ArrowRight":
          event.preventDefault();
          goToNext();
          break;
        case "+":
        case "=":
          event.preventDefault();
          zoomIn();
          break;
        case "-":
          event.preventDefault();
          zoomOut();
          break;
        case "0":
          event.preventDefault();
          resetZoom();
          break;
      }
    };

    if (isFullscreen) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [
    isFullscreen,
    goToNext,
    goToPrevious,
    zoomIn,
    zoomOut,
    resetZoom,
    onClose,
  ]);

  // Mouse drag handling for zoomed images
  const handleMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (!isZoomed) return;
      event.preventDefault();
      setIsDragging(true);
      setDragStart({
        x: event.clientX - dragPosition.x,
        y: event.clientY - dragPosition.y,
      });
    },
    [isZoomed, dragPosition]
  );

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (!isDragging || !isZoomed) return;
      event.preventDefault();
      setDragPosition({
        x: event.clientX - dragStart.x,
        y: event.clientY - dragStart.y,
      });
    },
    [isDragging, isZoomed, dragStart]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch handling for mobile
  const handleTouchStart = useCallback(
    (event: React.TouchEvent) => {
      if (!isZoomed || event.touches.length !== 1) return;
      const touch = event.touches[0];
      setIsDragging(true);
      setDragStart({
        x: touch.clientX - dragPosition.x,
        y: touch.clientY - dragPosition.y,
      });
    },
    [isZoomed, dragPosition]
  );

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!isDragging || !isZoomed || event.touches.length !== 1) return;
      event.preventDefault();
      const touch = event.touches[0];
      setDragPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    },
    [isDragging, isZoomed, dragStart]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Image click handlers
  const handleImageClick = useCallback(
    (event: React.MouseEvent) => {
      if (isFullscreen && !isZoomed) {
        // Double click to zoom
        if (event.detail === 2) {
          zoomIn();
        }
      } else if (!isFullscreen) {
        setIsFullscreen(true);
      }
    },
    [isFullscreen, isZoomed, zoomIn]
  );

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentIndex(index);
    setZoomLevel(1);
    setDragPosition({ x: 0, y: 0 });
    setIsZoomed(false);
  }, []);

  // Handle overlay keyboard events for accessibility
  const handleOverlayKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (
        event.key === "Escape" ||
        event.key === " " ||
        event.key === "Enter"
      ) {
        event.preventDefault();
        onClose ? onClose() : setIsFullscreen(false);
      }
    },
    [onClose]
  );

  if (totalImages === 0) {
    return null;
  }

  const currentVariant = hasVariants ? imageVariants[currentIndex] : undefined;
  const currentImageUrl = hasImages ? images[currentIndex] : undefined;

  const aspectRatioClass =
    styles[
      `aspect${aspectRatio.charAt(0).toUpperCase() + aspectRatio.slice(1)}`
    ];

  return (
    <>
      {/* Main Gallery */}
      <div className={`${styles.gallery} ${className} ${aspectRatioClass}`}>
        <div className={styles.mainImageContainer}>
          <button
            type="button"
            className={styles.mainImageButton}
            onClick={handleImageClick}
            aria-label={`View ${alt} - ${currentIndex + 1} of ${totalImages}`}
          >
            <ResponsiveImage
              variants={currentVariant}
              images={currentImageUrl ? [currentImageUrl] : undefined}
              alt={`${alt} - ${currentIndex + 1} of ${totalImages}`}
              className={styles.mainImage}
              size="large"
              loading="eager"
              priority={true}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 60vw"
            />
          </button>

          {/* Fullscreen Button */}
          {!hideFullscreenButton && (
            <button
              type="button"
              className={styles.fullscreenButton}
              onClick={() => setIsFullscreen(true)}
              aria-label={t("imageGallery.enterFullscreen")}
            >
              <MdFullscreen size={20} />
            </button>
          )}

          {/* Navigation buttons for multiple images */}
          {totalImages > 1 && (
            <>
              <button
                type="button"
                className={`${styles.navButton} ${styles.navButtonPrev}`}
                onClick={goToPrevious}
                aria-label={t("imageGallery.previousImage")}
              >
                <MdChevronLeft size={24} />
              </button>
              <button
                type="button"
                className={`${styles.navButton} ${styles.navButtonNext}`}
                onClick={goToNext}
                aria-label={t("imageGallery.nextImage")}
              >
                <MdChevronRight size={24} />
              </button>
            </>
          )}

          {/* Image counter */}
          {totalImages > 1 && (
            <div className={styles.imageCounter}>
              <Text variant="small">
                {currentIndex + 1} / {totalImages}
              </Text>
            </div>
          )}

          {/* Description Overlay (Non-fullscreen) */}
          {descriptions?.[currentIndex] && (
            <div className={styles.descriptionOverlay}>
              <Text variant="small" className={styles.descriptionText}>
                {descriptions[currentIndex]}
              </Text>
            </div>
          )}
        </div>

        {/* Thumbnails */}
        {showThumbnails && totalImages > 1 && (
          <div className={styles.thumbnailContainer}>
            {Array.from({ length: totalImages }).map((_, index) => {
              const thumbVariant = hasVariants
                ? imageVariants[index]
                : undefined;
              const thumbImageUrl = hasImages ? images[index] : undefined;
              const thumbKey =
                thumbVariant?.original?.url ||
                thumbImageUrl ||
                `thumb-${index}`;

              return (
                <button
                  type="button"
                  key={thumbKey}
                  className={`${styles.thumbnail} ${
                    index === currentIndex ? styles.thumbnailActive : ""
                  }`}
                  onClick={() => handleThumbnailClick(index)}
                  aria-label={t("imageGallery.selectImage", {
                    index: index + 1,
                  })}
                >
                  <ResponsiveImage
                    variants={thumbVariant}
                    images={thumbImageUrl ? [thumbImageUrl] : undefined}
                    alt={`${alt} thumbnail ${index + 1}`}
                    size="thumb"
                    loading="lazy"
                    className={styles.thumbnailImage}
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Fullscreen Overlay */}
      {isFullscreen && (
        <div
          className={styles.fullscreenOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose ? onClose() : setIsFullscreen(false);
            }
          }}
          onKeyDown={handleOverlayKeyDown}
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <div className={styles.fullscreenContainer}>
            {/* Fullscreen Header */}
            <div className={styles.fullscreenHeader}>
              {headerContent ? (
                <div className={styles.fullscreenCustomHeader}>
                  {headerContent}
                </div>
              ) : (
                <div className={styles.fullscreenTitle}>
                  <Text
                    variant="medium"
                    className={styles.fullscreenImageTitle}
                  >
                    {alt}
                  </Text>
                  <Text variant="small" className={styles.fullscreenSubtitle}>
                    {currentIndex + 1} of {totalImages}
                  </Text>
                  {descriptions?.[currentIndex] && (
                    <Text
                      variant="small"
                      className={styles.fullscreenDescription}
                    >
                      {descriptions[currentIndex]}
                    </Text>
                  )}
                </div>
              )}

              <div className={styles.fullscreenControls}>
                {/* Zoom controls */}
                <button
                  type="button"
                  className={styles.zoomButton}
                  onClick={zoomIn}
                  disabled={zoomLevel >= 3}
                  aria-label={t("imageGallery.zoomIn")}
                >
                  <MdZoomIn size={20} />
                </button>
                <button
                  type="button"
                  className={styles.zoomButton}
                  onClick={zoomOut}
                  disabled={zoomLevel <= 1}
                  aria-label={t("imageGallery.zoomOut")}
                >
                  <MdZoomOut size={20} />
                </button>

                {/* Close button */}
                <button
                  type="button"
                  className={styles.fullscreenCloseButton}
                  onClick={() => (onClose ? onClose() : setIsFullscreen(false))}
                  aria-label={t("imageGallery.exitFullscreen")}
                >
                  <MdClose size={24} />
                </button>
              </div>
            </div>

            {/* Fullscreen Image Container */}
            <div className={styles.fullscreenImageContainer}>
              <button
                type="button"
                className={styles.fullscreenImageButton}
                onClick={handleImageClick}
                aria-label={`View fullscreen ${alt} - ${currentIndex + 1} of ${totalImages}`}
                style={{
                  transform: `scale(${zoomLevel}) translate(${dragPosition.x / zoomLevel}px, ${dragPosition.y / zoomLevel}px)`,
                  cursor: isZoomed
                    ? isDragging
                      ? "grabbing"
                      : "grab"
                    : "zoom-in",
                }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {/* Use large variant for optimal fullscreen quality and performance */}
                <ResponsiveImage
                  variants={currentVariant}
                  images={currentImageUrl ? [currentImageUrl] : undefined}
                  alt={`${alt} - ${currentIndex + 1} of ${totalImages}`}
                  className={styles.fullscreenImage}
                  size="large"
                  loading="eager"
                  priority={true}
                  sizes="100vw"
                />
              </button>

              {/* Navigation in fullscreen */}
              {totalImages > 1 && (
                <>
                  <button
                    type="button"
                    className={`${styles.fullscreenNavButton} ${styles.fullscreenNavButtonPrev}`}
                    onClick={goToPrevious}
                    aria-label={t("imageGallery.previousImage")}
                  >
                    <MdChevronLeft size={32} />
                  </button>
                  <button
                    type="button"
                    className={`${styles.fullscreenNavButton} ${styles.fullscreenNavButtonNext}`}
                    onClick={goToNext}
                    aria-label={t("imageGallery.nextImage")}
                  >
                    <MdChevronRight size={32} />
                  </button>
                </>
              )}
            </div>

            {/* Fullscreen Instructions */}
            <div className={styles.fullscreenInstructions}>
              <Text variant="small" className={styles.instructionText}>
                {t("imageGallery.instructions")}
              </Text>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ImageGallery;
